// src/pages/Landing.js
import React from 'react';
import { Link } from 'react-router-dom';
import { useSelector } from 'react-redux';
import {
  FaArrowRight, FaQrcode, FaVideo, FaDownload,
  FaTrophy, FaShareAlt, FaMobile, FaUsers, FaRocket,
  FaMoneyBillWave, FaShieldAlt, FaHandPointer,
  FaMapMarkerAlt, FaLightbulb, FaPlay,
} from 'react-icons/fa';
import MainNavbar from '../components/common/MainNavbar';
import Footer from '../components/common/Footer';
import HeroImage from '../assets/images/hero.jpg';
import MissionImage from '../assets/images/mission.jpg';

/* ─── static data ─────────────────────────────────────────────────────────── */
const STEPS = [
  { n: '01', Icon: FaVideo,    title: 'Play Your Game',
    body: 'Visit any Akture-equipped court. Our cameras capture every moment automatically — no app, no setup.' },
  { n: '02', Icon: FaQrcode,   title: 'Scan the QR Code',
    body: 'After the final buzzer, scan the courtside QR code to instantly access your session footage.' },
  { n: '03', Icon: FaDownload, title: 'Purchase & Download',
    body: 'Select your highlights, purchase in seconds, download in HD, and post straight to social media.' },
];

const PLAYER_BENEFITS = [
  { n: '01', Icon: FaTrophy,   title: 'Competitive Advantage',
    body: "Enhance your game with instant access to high-definition footage. Whether you're striving for precision or mastering technique, our platform helps you spot opportunities for improvement." },
  { n: '02', Icon: FaUsers,    title: 'Relive the Moment',
    body: 'Never miss a moment. Go back and relive those game-changing plays, clutch shots, and defining victories anytime, anywhere.' },
  { n: '03', Icon: FaShareAlt, title: 'Share',
    body: 'Celebrate with your community. Showcase your standout moments with teammates, coaches, and friends in just a few clicks.' },
  { n: '04', Icon: FaMobile,   title: 'Easy to Use',
    body: 'Accessing your footage is as simple as scanning our QR code after your game, selecting the time and court you played on, purchasing your video, and accessing it via your Akture Dashboard.' },
];

const VENUE_BENEFITS = [
  { n: '01', Icon: FaMoneyBillWave, title: 'Novel source of income',
    body: 'Using emerging technologies and our proprietary software, every memory created on your courts or fields can now be purchased — generating an entirely new source of income for your business.' },
  { n: '02', Icon: FaTrophy,        title: 'Competitive advantage',
    body: 'Once our system is installed, players will be incentivized to return to your location where they can purchase their precious memories, as opposed to your competitors where those memories would be lost.' },
  { n: '03', Icon: FaShieldAlt,     title: 'Security',
    body: 'Our camera systems provide added security and can replace your existing security setup at better costs.' },
  { n: '04', Icon: FaHandPointer,   title: 'Easy to use',
    body: 'Once a guest has finished their game, they simply scan our QR code. They can then purchase their video and quickly access their footage to analyze and share on social media.' },
];

const STATS = [
  { value: '50+',    label: 'Active Courts' },
  { value: '10K+',   label: 'Videos Sold' },
  { value: '4.9★',   label: 'Avg Rating' },
  { value: '<3 min', label: 'Access Time' },
];

/* ─── tiny shared pieces ───────────────────────────────────────────────────── */
const Eyebrow = ({ children, color = '#BD2026' }) => (
  <p style={{ color }} className="text-[11px] font-black uppercase tracking-[0.22em] mb-3 leading-none">
    {children}
  </p>
);

const SectionHeading = ({ children, className = '' }) => (
  <h2 className={`text-3xl md:text-4xl font-extrabold leading-tight ${className}`}>{children}</h2>
);

/* ─── component ───────────────────────────────────────────────────────────── */
const Landing = () => {
  const { user } = useSelector((s) => s.auth);
  const isAuth = !!user;

  return (
    <div className="flex flex-col min-h-screen bg-white overflow-x-hidden">
      <MainNavbar user={user} />

      {/* ════════════════════════════════════════════════════════════════════
          §1  HERO
          ──────────────────────────────────────────────────────────────────
          Full-viewport hero. Background + overlay are inline-style so they
          are never tree-shaken by Tailwind JIT. Content is left-aligned on
          a dark column; the photo bleeds through on the right half.
         ════════════════════════════════════════════════════════════════════ */}
      <section
        className="relative flex items-center overflow-hidden"
        style={{ minHeight: '100vh', backgroundImage: `url(${HeroImage})`, backgroundSize: 'cover', backgroundPosition: 'center' }}
      >
        {/* Overlay: strong on the left, fades right */}
        <div
          className="absolute inset-0"
          style={{ background: 'linear-gradient(105deg, rgba(0,0,0,0.92) 0%, rgba(0,0,0,0.78) 40%, rgba(0,0,0,0.38) 75%, rgba(0,0,0,0.15) 100%)' }}
        />

        {/* Bottom fade so stats-bar section blends in */}
        <div className="absolute bottom-0 inset-x-0 h-32 pointer-events-none"
          style={{ background: 'linear-gradient(to bottom, transparent, rgba(0,0,0,0.55))' }} />

        <div className="relative z-10 w-full max-w-screen-xl mx-auto px-6 md:px-12 pt-36 pb-32">
          {/* Eyebrow badge */}
          <div
            className="inline-flex items-center gap-2.5 px-4 py-2 rounded-full text-white text-xs font-semibold tracking-wide mb-10"
            style={{ background: 'rgba(255,255,255,0.10)', border: '1px solid rgba(255,255,255,0.22)' }}
          >
            <span className="w-2 h-2 rounded-full bg-green-400 animate-pulse shrink-0" />
            Live in Texas — expanding fast
          </div>

          {/* Headline */}
          <h1
            className="font-black text-white tracking-tight leading-none mb-6"
            style={{ fontSize: 'clamp(2.8rem, 6vw, 5.5rem)', textShadow: '0 2px 20px rgba(0,0,0,0.5)' }}
          >
            ALL YOUR MOMENTS,
            <br />
            <span
              style={{
                background: 'linear-gradient(95deg, #BD2026 20%, #FF6F3C 100%)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                backgroundClip: 'text',
              }}
            >
              PERFECTLY PRESERVED
            </span>
          </h1>

          {/* Sub-headline */}
          <p
            className="text-xl md:text-2xl font-light leading-snug mb-12 max-w-lg"
            style={{ color: 'rgba(255,255,255,0.80)', textShadow: '0 1px 8px rgba(0,0,0,0.4)' }}
          >
            Your best sports memories, at your fingertips.
          </p>

          {/* CTAs */}
          <div className="flex flex-wrap gap-4">
            {isAuth ? (
              <Link
                to="/dashboard"
                className="inline-flex items-center gap-2.5 px-8 py-4 rounded-xl font-bold text-white text-sm transition-all"
                style={{ background: '#BD2026', boxShadow: '0 4px 24px rgba(189,32,38,0.45)' }}
              >
                Go to Dashboard <FaArrowRight />
              </Link>
            ) : (
              <>
                <Link
                  to="/explore"
                  className="inline-flex items-center gap-2.5 px-8 py-4 rounded-xl font-bold text-white text-sm transition-all"
                  style={{ background: '#BD2026', boxShadow: '0 4px 24px rgba(189,32,38,0.45)' }}
                >
                  Explore Courts <FaArrowRight />
                </Link>
                <Link
                  to="/register"
                  className="inline-flex items-center gap-2.5 px-8 py-4 rounded-xl font-semibold text-white text-sm transition-all"
                  style={{ background: 'rgba(255,255,255,0.12)', border: '1.5px solid rgba(255,255,255,0.30)' }}
                >
                  Create Free Account
                </Link>
              </>
            )}
          </div>
        </div>

        {/* Scroll cue */}
        <div className="absolute bottom-8 left-1/2 -translate-x-1/2 flex flex-col items-center gap-1 z-10"
          style={{ opacity: 0.5 }}>
          <div className="w-px h-8 bg-white" />
          <span className="text-white text-[9px] uppercase tracking-widest mt-1">Scroll</span>
        </div>
      </section>

      {/* ════════════════════════════════════════════════════════════════════
          §2  STATS BAR
         ════════════════════════════════════════════════════════════════════ */}
      <div className="bg-gray-950 py-12 px-6">
        <div className="max-w-4xl mx-auto grid grid-cols-2 md:grid-cols-4 gap-8 text-center">
          {STATS.map(({ value, label }) => (
            <div key={label} className="flex flex-col items-center gap-1">
              <span className="text-4xl font-black" style={{ color: '#BD2026' }}>{value}</span>
              <span className="text-xs font-semibold uppercase tracking-widest" style={{ color: 'rgba(255,255,255,0.45)' }}>{label}</span>
            </div>
          ))}
        </div>
      </div>

      {/* ════════════════════════════════════════════════════════════════════
          §3  HOW IT WORKS — numbered icon steps with connecting rule
         ════════════════════════════════════════════════════════════════════ */}
      <section className="py-24 px-6 bg-white">
        <div className="max-w-5xl mx-auto">
          {/* Header */}
          <div className="text-center mb-16">
            <Eyebrow>Simple process</Eyebrow>
            <SectionHeading className="text-gray-900">Court to phone in 3 steps</SectionHeading>
            <p className="text-gray-500 mt-4 max-w-md mx-auto text-sm leading-relaxed">
              No complicated setup. No waiting days. Just play, scan, own your highlights.
            </p>
          </div>

          {/* Steps */}
          <div className="grid md:grid-cols-3 gap-8 md:gap-6 relative">
            {/* Connector line (desktop only) */}
            <div
              className="hidden md:block absolute"
              style={{ top: 28, left: '20%', right: '20%', height: 1, background: 'linear-gradient(to right, #BD2026 0%, #FF6F3C 50%, #00A693 100%)', opacity: 0.25 }}
            />
            {STEPS.map(({ n, Icon, title, body }) => (
              <div key={n} className="relative flex flex-col items-center text-center px-4">
                {/* Icon circle */}
                <div
                  className="w-14 h-14 rounded-2xl flex items-center justify-center text-white text-xl mb-5 relative z-10"
                  style={{ background: 'linear-gradient(135deg, #BD2026, #FF6F3C)', boxShadow: '0 8px 24px rgba(189,32,38,0.25)' }}
                >
                  <Icon />
                  {/* Step number badge */}
                  <span
                    className="absolute -top-2.5 -right-2.5 w-5 h-5 rounded-full text-white flex items-center justify-center font-black"
                    style={{ fontSize: 9, background: '#111', border: '2px solid #fff' }}
                  >
                    {n}
                  </span>
                </div>
                <h3 className="font-bold text-gray-900 text-base mb-2">{title}</h3>
                <p className="text-sm text-gray-500 leading-relaxed">{body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ════════════════════════════════════════════════════════════════════
          §4  OUR MISSION
          ──────────────────────────────────────────────────────────────────
          Two-column: editorial text on left (with red left-border accent),
          image on right with slight overlap shadow.
         ════════════════════════════════════════════════════════════════════ */}
      <section className="py-24 px-6 bg-gray-50">
        <div className="max-w-6xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-16 items-center">

            {/* Text column */}
            <div>
              {/* Icon + eyebrow */}
              <div className="flex items-center gap-3 mb-6">
                <div
                  className="w-10 h-10 rounded-xl flex items-center justify-center shrink-0"
                  style={{ background: '#fef2f2', color: '#BD2026' }}
                >
                  <FaLightbulb className="text-lg" />
                </div>
                <Eyebrow>Our Mission</Eyebrow>
              </div>

              {/* Heading */}
              <SectionHeading className="text-gray-900">Empowering every athlete</SectionHeading>

              {/* Accent rule */}
              <div className="flex items-start mt-6 gap-5">
                <div className="w-1 shrink-0 rounded-full self-stretch" style={{ background: '#BD2026', minHeight: 80 }} />
                {/* Body text — unchanged */}
                <p className="text-gray-600 text-base leading-relaxed">
                  We aim to empower every athlete by making high-quality sports footage accessible to all,
                  while fostering a strong, connected local sports community. Your moments deserve to be
                  celebrated, studied, and shared without barriers.
                </p>
              </div>

              {/* Inline stat pair */}
              <div className="flex gap-10 mt-10">
                <div>
                  <p className="text-3xl font-black" style={{ color: '#BD2026' }}>10K+</p>
                  <p className="text-xs text-gray-400 font-semibold uppercase tracking-widest mt-0.5">Videos sold</p>
                </div>
                <div className="w-px bg-gray-200" />
                <div>
                  <p className="text-3xl font-black text-gray-900">50+</p>
                  <p className="text-xs text-gray-400 font-semibold uppercase tracking-widest mt-0.5">Courts live</p>
                </div>
              </div>
            </div>

            {/* Image column */}
            <div
              className="relative rounded-2xl overflow-hidden"
              style={{ boxShadow: '0 24px 64px rgba(0,0,0,0.14)', aspectRatio: '4/3' }}
            >
              <img
                src={MissionImage}
                alt="Athletes on court"
                className="w-full h-full object-cover"
              />
              {/* Bottom gradient overlay on image */}
              <div
                className="absolute inset-0"
                style={{ background: 'linear-gradient(to top, rgba(0,0,0,0.30) 0%, transparent 55%)' }}
              />
            </div>
          </div>
        </div>
      </section>

      {/* ════════════════════════════════════════════════════════════════════
          §5  OUR VISION
          ──────────────────────────────────────────────────────────────────
          Video takes 60 % of desktop width. Text is supporting context.
         ════════════════════════════════════════════════════════════════════ */}
      <section className="py-24 px-6 bg-white">
        <div className="max-w-6xl mx-auto">
          <div className="grid lg:grid-cols-5 gap-14 items-center">

            {/* Video — wider column (3/5) */}
            <div className="lg:col-span-3">
              <div
                className="rounded-2xl overflow-hidden"
                style={{ position: 'relative', paddingBottom: '56.25%', height: 0, boxShadow: '0 20px 60px rgba(0,0,0,0.12)' }}
              >
                <iframe
                  style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', border: 0 }}
                  src="https://www.youtube.com/embed/RXuJuz5fkow"
                  title="Akture Sports Vision"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                />
              </div>
            </div>

            {/* Text — narrower column (2/5) */}
            <div className="lg:col-span-2">
              <div className="flex items-center gap-3 mb-6">
                <div
                  className="w-10 h-10 rounded-xl flex items-center justify-center shrink-0"
                  style={{ background: '#f0fdf8', color: '#00A693' }}
                >
                  <FaRocket className="text-lg" />
                </div>
                <Eyebrow color="#00A693">Our Vision</Eyebrow>
              </div>

              <SectionHeading className="text-gray-900">Access for all athletes</SectionHeading>

              <div className="flex items-start mt-6 gap-5">
                <div className="w-1 shrink-0 rounded-full self-stretch" style={{ background: '#00A693', minHeight: 64 }} />
                <p className="text-gray-600 text-base leading-relaxed">
                  We envision a world where every athlete has access to their sports footage,
                  allowing them to relive their best moments, share their highlights, and improve their game.
                </p>
              </div>

              {/* CTA chip */}
              <Link
                to="/explore"
                className="inline-flex items-center gap-2 mt-8 text-sm font-bold transition-colors"
                style={{ color: '#00A693' }}
              >
                <FaPlay style={{ fontSize: 10 }} /> See it in action
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* ════════════════════════════════════════════════════════════════════
          §6  FOR PLAYERS — Competitive Advantage
          ──────────────────────────────────────────────────────────────────
          Cards with top accent, step number, icon, title, body.
         ════════════════════════════════════════════════════════════════════ */}
      <section className="py-24 px-6 bg-gray-50">
        <div className="max-w-6xl mx-auto">
          {/* Section header */}
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-14">
            <div>
              <Eyebrow>For Athletes</Eyebrow>
              <SectionHeading className="text-gray-900">For Players</SectionHeading>
            </div>
            <p className="text-sm text-gray-500 max-w-xs leading-relaxed md:text-right">
              Everything you need to relive, study, and share your game.
            </p>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {PLAYER_BENEFITS.map(({ n, Icon, title, body }) => (
              <div
                key={n}
                className="bg-white rounded-2xl overflow-hidden flex flex-col transition-all duration-300 hover:-translate-y-1"
                style={{ border: '1px solid #f0f0f0', boxShadow: '0 2px 12px rgba(0,0,0,0.05)' }}
              >
                {/* Top accent bar */}
                <div style={{ height: 4, background: 'linear-gradient(to right, #BD2026, #FF6F3C)' }} />

                <div className="p-6 flex flex-col flex-1">
                  {/* Step number + icon row */}
                  <div className="flex items-center justify-between mb-4">
                    <div
                      className="w-11 h-11 rounded-xl flex items-center justify-center text-lg text-white"
                      style={{ background: 'linear-gradient(135deg, #BD2026, #FF6F3C)' }}
                    >
                      <Icon />
                    </div>
                    <span className="text-3xl font-black" style={{ color: '#f0f0f0' }}>{n}</span>
                  </div>

                  <h3 className="font-bold text-gray-900 text-sm mb-2">{title}</h3>
                  <p className="text-sm text-gray-500 leading-relaxed flex-1">{body}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ════════════════════════════════════════════════════════════════════
          §7  FOR VENUES
          ──────────────────────────────────────────────────────────────────
          Dark section. Cards have a coloured top accent and orange icon.
         ════════════════════════════════════════════════════════════════════ */}
      <section className="py-24 px-6" style={{ background: '#0d0d0d' }}>
        <div className="max-w-6xl mx-auto">
          {/* Section header */}
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-14">
            <div>
              <Eyebrow color="#FF6F3C">For Venues</Eyebrow>
              <SectionHeading className="text-white">For Venues</SectionHeading>
            </div>
            <p className="text-sm max-w-xs leading-relaxed md:text-right" style={{ color: 'rgba(255,255,255,0.45)' }}>
              Turn every game played on your courts into a revenue opportunity.
            </p>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {VENUE_BENEFITS.map(({ n, Icon, title, body }) => (
              <div
                key={n}
                className="rounded-2xl overflow-hidden flex flex-col transition-all duration-300 hover:-translate-y-1"
                style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.09)' }}
              >
                {/* Top accent */}
                <div style={{ height: 3, background: 'linear-gradient(to right, #FF6F3C, #ffaa00)' }} />

                <div className="p-6 flex flex-col flex-1">
                  <div className="flex items-center justify-between mb-4">
                    <div
                      className="w-11 h-11 rounded-xl flex items-center justify-center text-lg"
                      style={{ background: 'rgba(255,111,60,0.15)', color: '#FF6F3C' }}
                    >
                      <Icon />
                    </div>
                    <span className="text-3xl font-black" style={{ color: 'rgba(255,255,255,0.07)' }}>{n}</span>
                  </div>

                  <h3 className="font-bold text-white text-sm mb-2">{title}</h3>
                  <p className="text-sm leading-relaxed flex-1" style={{ color: 'rgba(255,255,255,0.50)' }}>{body}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ════════════════════════════════════════════════════════════════════
          §8  LOCATIONS
         ════════════════════════════════════════════════════════════════════ */}
      <section className="py-24 px-6 bg-gray-50">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-12">
            <Eyebrow>Locations</Eyebrow>
            <SectionHeading className="text-gray-900">
              <FaMapMarkerAlt className="inline mr-2 align-middle" style={{ color: '#BD2026', fontSize: '1.75rem' }} />
              Find us near you
            </SectionHeading>
          </div>
          <div className="rounded-2xl overflow-hidden border border-gray-100" style={{ height: 440, boxShadow: '0 8px 40px rgba(0,0,0,0.10)' }}>
            <iframe
              title="Akture Basketball Court Locations"
              src="https://www.google.com/maps/d/u/0/embed?mid=12avFMj2EBdv5CmxpPm4xoCOGCmK3HoA&ehbc=2E312F&noprof=1"
              width="100%"
              height="100%"
              style={{ border: 0 }}
              allowFullScreen
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
            />
          </div>
        </div>
      </section>

      {/* ════════════════════════════════════════════════════════════════════
          §9  FINAL CTA
         ════════════════════════════════════════════════════════════════════ */}
      <section
        className="relative py-28 px-6 text-white text-center overflow-hidden"
        style={{ background: 'linear-gradient(135deg, #BD2026 0%, #7a0d10 55%, #010101 100%)' }}
      >
        {/* Dot grid texture */}
        <div
          className="absolute inset-0 pointer-events-none"
          style={{ backgroundImage: 'radial-gradient(circle, rgba(255,255,255,0.06) 1px, transparent 1px)', backgroundSize: '28px 28px' }}
        />
        <div className="relative z-10 max-w-xl mx-auto">
          <p className="text-[11px] font-black uppercase tracking-[0.22em] mb-5" style={{ color: 'rgba(255,255,255,0.50)' }}>
            Get started today
          </p>
          <h2 className="text-4xl md:text-5xl font-black leading-tight mb-4">
            Ready to capture your moments?
          </h2>
          <p className="text-lg mb-12 max-w-sm mx-auto leading-relaxed" style={{ color: 'rgba(255,255,255,0.65)' }}>
            Join thousands of athletes already using Akture.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            {isAuth ? (
              <Link
                to="/dashboard"
                className="inline-flex items-center justify-center gap-2 px-10 py-4 bg-white font-bold rounded-xl hover:bg-gray-100 transition-colors"
                style={{ color: '#BD2026' }}
              >
                Go to Dashboard <FaArrowRight />
              </Link>
            ) : (
              <>
                <Link
                  to="/register"
                  className="inline-flex items-center justify-center gap-2 px-10 py-4 bg-white font-bold rounded-xl hover:bg-gray-100 transition-colors"
                  style={{ color: '#BD2026' }}
                >
                  Get Started Free <FaArrowRight />
                </Link>
                <Link
                  to="/explore"
                  className="inline-flex items-center justify-center gap-2 px-10 py-4 rounded-xl font-semibold text-white border transition-colors"
                  style={{ background: 'rgba(255,255,255,0.10)', borderColor: 'rgba(255,255,255,0.28)' }}
                >
                  Browse Courts
                </Link>
              </>
            )}
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default Landing;
